import { ClipboardCheck, X } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";

import { Button, Dialog, Spinner } from "@/components/ui";
import { VideoProjectQaStudio } from "@/containers/Editor/VideoProjectPage/components/VideoProjectQaStudio";
import { isQaStudioReadOnly } from "@/containers/Editor/VideoProjectPage/qaReview";
import { EDITOR_VIDEO_PROJECT_EXTRA_FIELDS, VideoProject, VideoProjectServices } from "@/services/bedrock";
import { formatHexColor } from "@/utils";
import { buildPreferenceOptions } from "@/utils/projectHelpers";

interface QueueQaStudioProps {
  projectId: number | null;
  onOpenChange: (open: boolean) => void;
  onStatusChange: (project: VideoProject) => void;
}

function qaStudioMessage(project: VideoProject) {
  const networks = project.post?.postSocialNetworks ?? [];
  const withCaption = networks.some(network => network.caption && network.caption.length > 0);
  if (!withCaption) return project.post?.message || "";
  return networks[0]?.caption || "";
}

export function QueueQaStudio({ projectId, onOpenChange, onStatusChange }: QueueQaStudioProps) {
  const [project, setProject] = React.useState<VideoProject | undefined>();
  const [loading, setLoading] = React.useState(false);
  const onOpenChangeRef = React.useRef(onOpenChange);
  const onStatusChangeRef = React.useRef(onStatusChange);
  onOpenChangeRef.current = onOpenChange;
  onStatusChangeRef.current = onStatusChange;

  React.useEffect(() => {
    if (projectId == null) {
      setProject(undefined);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setProject(undefined);
    setLoading(true);
    VideoProjectServices.show(projectId, EDITOR_VIDEO_PROJECT_EXTRA_FIELDS)
      .then(nextProject => {
        if (!cancelled) setProject(nextProject);
      })
      .catch(() => {
        if (cancelled) return;
        toast.error("Failed to open QA Studio");
        onOpenChangeRef.current(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const setVideoProject = React.useCallback<React.Dispatch<React.SetStateAction<VideoProject | undefined>>>(updater => {
    setProject(current => {
      const next = typeof updater === "function" ? updater(current) : updater;
      if (current && next && next.status !== current.status) {
        onStatusChangeRef.current(next);
      }
      return next;
    });
  }, []);

  if (projectId == null) return null;

  if (!project) {
    if (!loading) return null;
    return (
      <Dialog
        open
        onOpenChange={onOpenChange}
        size="full"
        disableEscapeDismissal
        className="flex h-[calc(100vh-2rem)] max-h-[100vh] min-h-0 w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl"
      >
        <Dialog.Header className="shrink-0 px-5 py-3">
          <div className="flex h-14 min-w-0 items-center gap-3">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-500">
              <ClipboardCheck className="size-4" />
            </div>
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-base font-bold text-slate-800">QA Studio</Dialog.Title>
              <p className="truncate text-sm font-medium text-slate-500">Video Project #{projectId}</p>
            </div>
            <Dialog.Close
              render={
                <Button variant="ghost" size="icon-sm" className="shrink-0">
                  <X />
                  <span className="sr-only">Close</span>
                </Button>
              }
            />
          </div>
        </Dialog.Header>
        <Dialog.Content className="flex min-h-0 flex-1 items-center justify-center">
          <Spinner className="size-8" />
        </Dialog.Content>
      </Dialog>
    );
  }

  return (
    <VideoProjectQaStudio
      open
      onOpenChange={onOpenChange}
      videoProject={project}
      setVideoProject={setVideoProject}
      playerNotes={project.clientNotes}
      coachNotes={project.coachClientNotes}
      preferencesInfo={buildPreferenceOptions(project.preferences)}
      formatHexColor={formatHexColor}
      videoProjectMessages={project.messages}
      finalMessage={qaStudioMessage(project)}
      hashtags={project.post?.hashtags || ""}
      readOnly={isQaStudioReadOnly(project.status, project.archived)}
    />
  );
}
